import React, { useMemo, useState } from 'react';
import { Upload, Loader2, FileSpreadsheet, ChevronDown, ChevronRight } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { organisationsAPI } from '../../services/api';
import toast from '../../services/toast';

function parseCsvText(text) {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (!lines.length) return { headers: [], rows: [] };
  const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
  const rows: { rowNumber: number; [key: string]: string | number }[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(',').map((c) => c.trim().replace(/^"|"$/g, ''));
    const row = {};
    headers.forEach((h, idx) => {
      row[h] = cols[idx] ?? '';
    });
    rows.push({ rowNumber: i + 1, ...row });
  }
  return { headers, rows };
}

function downloadTemplate() {
  const blob = new Blob(['email,name,role\n'], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'members-import-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

export default function MemberImportModal({ isOpen, onClose, orgId, onSuccess }: any) {
  const [file, setFile] = useState<any>(null);
  const [parsedRows, setParsedRows] = useState<any[]>([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [errorsOpen, setErrorsOpen] = useState(true);

  const previewRows = useMemo(() => parsedRows.filter((r) => r.email).slice(0, 50), [parsedRows]);

  const reset = () => {
    setFile(null);
    setParsedRows([]);
    setResult(null);
    setErrorsOpen(true);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const onFile = async (f) => {
    if (!f) return;
    setFile(f);
    setResult(null);
    try {
      const text = await f.text();
      const { rows } = parseCsvText(text);
      setParsedRows(rows);
      if (!rows.length) toast.error('No data rows found in CSV');
    } catch (e) {
      toast.error((e as Error).message || 'Could not read file');
      setParsedRows([]);
    }
  };

  const handleImport = async () => {
    if (!orgId || !file) return;
    setImporting(true);
    try {
      const data = await organisationsAPI.importMembersCsv(orgId, file);
      setResult(data);
      toast.success('Import finished');
      onSuccess?.();
    } catch (e) {
      toast.error((e as Error).message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const errors = result?.errors ?? [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Import members"
      icon={<FileSpreadsheet className="w-5 h-5 text-vivid-red" />}
    >
      <div className="p-4 space-y-4 max-h-[75vh] overflow-y-auto">
        <p className="text-sm text-slate-400">
          Upload a CSV with columns <code className="text-xs">email</code> (required),{' '}
          <code className="text-xs">name</code>, and <code className="text-xs">role</code>{' '}
          (optional).
        </p>
        <button type="button" className="btn-secondary-mdl text-sm" onClick={downloadTemplate}>
          Download CSV template
        </button>

        <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-dark-border p-8 cursor-pointer hover:bg-dark-elevated/50">
          <Upload className="h-8 w-8 text-slate-400" aria-hidden />
          <span className="text-sm text-slate-300">
            {file ? file.name : 'Choose or drop a CSV file'}
          </span>
          <input
            type="file"
            accept=".csv,text/csv"
            className="sr-only"
            onChange={(e) => onFile(e.target.files?.[0])}
          />
        </label>

        {previewRows.length > 0 && !result ? (
          <div>
            <h3 className="text-sm font-semibold text-slate-100 mb-2">
              Preview ({parsedRows.length} row{parsedRows.length === 1 ? '' : 's'})
            </h3>
            <div className="overflow-x-auto rounded-xl border border-dark-border">
              <table className="w-full text-left text-sm">
                <thead className="bg-dark-base/50">
                  <tr>
                    <th className="px-3 py-2 font-medium">Email</th>
                    <th className="px-3 py-2 font-medium">Name</th>
                    <th className="px-3 py-2 font-medium">Role</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((r) => (
                    <tr key={r.rowNumber} className="border-t border-gray-100">
                      <td className="px-3 py-2">{r.email}</td>
                      <td className="px-3 py-2">{r.name || '—'}</td>
                      <td className="px-3 py-2">{r.role || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {parsedRows.length > 50 ? (
              <p className="mt-1 text-xs text-slate-500">Showing first 50 rows.</p>
            ) : null}
          </div>
        ) : null}

        {result ? (
          <div className="rounded-xl border border-dark-border bg-dark-base p-4 text-sm/50">
            <p className="font-medium text-slate-100">Import summary</p>
            <ul className="mt-2 space-y-1 text-slate-300">
              <li>{result.imported ?? 0} member(s) added</li>
              <li>{result.invited ?? 0} invite(s) sent</li>
              <li>{result.skipped ?? 0} skipped</li>
            </ul>
            {errors.length > 0 ? (
              <div className="mt-3">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-sm font-medium text-vivid-red"
                  onClick={() => setErrorsOpen((o) => !o)}
                >
                  {errorsOpen ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                  {errors.length} error{errors.length === 1 ? '' : 's'}
                </button>
                {errorsOpen ? (
                  <ul className="mt-2 max-h-40 overflow-y-auto text-red-300 space-y-1">
                    {errors.map((err, i) => (
                      <li key={`${err.row}-${i}`}>
                        Row {err.row}: {err.reason}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary-mdl" onClick={handleClose}>
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result ? (
            <button
              type="button"
              className="btn-primary-mdl inline-flex items-center gap-2"
              disabled={!file || !parsedRows.length || importing}
              onClick={handleImport}
            >
              {importing ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
              Import
            </button>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
